import { Loader } from '@components';
import { usePolling } from 'core/hooks/usePolling';
import {
  useAuthStore,
  useCheckoutStore,
  useErrorTrackerHandler,
  useTranslation,
} from 'mycheck-core';
import { HttpCheckoutApiResponse } from 'plugins/CheckoutPlugin/types/CheckoutTypes';
import React, { RefObject, useEffect } from 'react';
import styles from './AstralWallet.module.scss';
import { FormikProps } from 'formik';
import { IFormikValues } from 'plugins/CheckoutPlugin/types/IFormikValues';
import { useAstralWalletIframeMessageListener } from './hooks/useAstralWalletIframeMessageListener';
import { ASTRAL_WALLET_IFRAME_ID } from './constants';

interface AstralWalletProps {
  handleCloseModal: () => void;
  formikRef: RefObject<FormikProps<IFormikValues>>;
}

const AstralWallet: React.FC<AstralWalletProps> = ({
  handleCloseModal,
  formikRef,
}) => {
  const CheckoutStore = useCheckoutStore();
  const AuthStore = useAuthStore();
  const errorHandler = useErrorTrackerHandler();
  const { t } = useTranslation();

  useAstralWalletIframeMessageListener({
    formikRef,
    handleCloseIframe: handleCloseModal,
  });

  const onErrorCallback = (err: Error) => {
    handleCloseModal();
    errorHandler.onError(err);
  };

  const fetchAstralWalletIframeData = () => {
    return CheckoutStore.getAstralWalletIframeData(
      { paymentId: CheckoutStore.astralWalletTransactionData.paymentId },
      AuthStore.accessToken,
    );
  };

  const {
    start: startFetchAstralWalletIFrameDataInterval,
    stop: stopFetchAstralWalletIFrameDataInterval,
    isPolling,
  } = usePolling<HttpCheckoutApiResponse['iGetAstralWalletIframeData']>({
    pollFn: fetchAstralWalletIframeData,
    options: {
      shouldStopFn: (result) => {
        return result.url !== null;
      },
      onErrorCallback,
    },
  });

  useEffect(() => {
    CheckoutStore.setIsAstralWalletLocked(false);
    startFetchAstralWalletIFrameDataInterval();

    return () => stopFetchAstralWalletIFrameDataInterval();
  }, []);

  if (isPolling || !CheckoutStore.astralWalletIframeData.url) {
    return (
      <div className={styles.astralWalletIframeLoaderWrapper}>
        <Loader label={t('astralWallet.loadingLabel')} withOverlay={false} />
      </div>
    );
  }

  return (
    <iframe
      id={ASTRAL_WALLET_IFRAME_ID}
      allow="payment"
      title={t('astralWallet.iframeTitle')}
      className={styles.astralWalletIframe}
      src={CheckoutStore.astralWalletIframeData.url}
    />
  );
};

export default AstralWallet;
